import React, { useState, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  CheckCircle,
  Clock,
  AlertCircle,
  Receipt,
  FileText,
  ArrowRight,
  Search,
  X,
  CalendarDays,
  TrendingUp,
  Settings2,
} from "lucide-react";
import type { BillingMilestone, BillingMilestoneStatus, BillingTriggerType } from "@/types/budget.types";

// ============================================================================
// Types
// ============================================================================

interface Invoice {
  id: number;
  invoiceNumber: string | null;
  date: string | null;
  description: string;
  amount: number;
  due: string | null;
  status: "Draft" | "Sent" | "Paid" | "Overdue";
  /** Optional: ID of the milestone this invoice was raised against. When present,
   *  matching uses ID instead of amount so milestones with equal amounts don't
   *  incorrectly inherit each other's paid status. */
  milestoneId?: number;
}

interface FixedFeeBillingTabProps {
  /** Contract value from the Sales Budget */
  contractValue: number;
  /** Billing milestones from the Sales Budget */
  milestones: BillingMilestone[];
  /** Invoices already raised for this project */
  invoices: Invoice[];
  /** Project name — used in wizard header */
  projectName: string;
  /** Project completion % (0–100) — used to evaluate percent-complete milestone status */
  completionPercent: number;
  /**
   * Project-level billing trigger type (Phase 7+).
   * Determines how ALL milestones on this project are evaluated.
   * Defaults to 'date' for backwards compatibility.
   */
  billingTriggerType?: BillingTriggerType;
  /** Called when user clicks "Generate Invoice" on a milestone row */
  onGenerateInvoice: (milestone: BillingMilestone) => void;
  /** Called when user clicks "Billing Settings" */
  onSettingsClick?: () => void;
  /** Currency formatter */
  formatCurrency: (value: number) => string;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Derive the display status for a milestone row.
 *
 * For `date` milestones the logic is unchanged — compare dueDate to today.
 * For `percent-complete` milestones:
 *   - `paid`      — matching Paid invoice exists
 *   - `due`       — matching Sent invoice exists (already invoiced)
 *   - `due`       — threshold met (completionPercent >= triggerPercent), not yet invoiced
 *   - `overdue`   — threshold met AND optional deadline date has passed; OR
 *                   threshold NOT met but deadline date has passed
 *   - `pending`   — threshold not yet reached and no deadline passed
 */
function getMilestoneDisplayStatus(
  milestone: BillingMilestone,
  invoices: Invoice[],
  completionPercent: number,
  billingTriggerType: BillingTriggerType = 'date'
): BillingMilestoneStatus {
  // Match invoices — prefer by milestoneId to avoid false positives on equal amounts.
  const invoicesForMilestone = invoices.filter((inv) =>
    inv.milestoneId !== undefined
      ? inv.milestoneId === milestone.id
      : Math.abs(inv.amount - milestone.amount) < 0.01
  );

  const hasMatchingInvoice = invoicesForMilestone.some(
    (inv) => inv.status === "Sent" || inv.status === "Paid"
  );

  if (hasMatchingInvoice) {
    const paidInvoice = invoicesForMilestone.find((inv) => inv.status === "Paid");
    return paidInvoice ? "paid" : "due";
  }

  // — % Complete milestone logic — driven by project-level billingTriggerType (Phase 7+)
  if (billingTriggerType === "percent-complete") {
    const thresholdMet =
      milestone.triggerPercent !== undefined &&
      completionPercent >= milestone.triggerPercent;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const deadlinePassed =
      milestone.dueDate
        ? new Date(milestone.dueDate) < today
        : false;

    if (thresholdMet && deadlinePassed) return "overdue"; // Met but deadline blown
    if (!thresholdMet && deadlinePassed) return "overdue"; // Deadline passed before reaching threshold
    if (thresholdMet) return "due";                         // Ready to invoice
    return "pending";                                        // Not yet reached
  }

  // — Date milestone logic (unchanged) —
  return milestone.status;
}

/**
 * Returns the status badge for a milestone row.
 *
 * `hasMatchingInvoice` is used to disambiguate "due" which can mean either
 * "has a Sent invoice" OR "threshold met, not yet invoiced". Only the former
 * should show "Invoiced" — the latter shows "Due" to prompt action.
 */
function getMilestoneStatusBadge(
  status: BillingMilestoneStatus,
  hasMatchingInvoice = false,
) {
  switch (status) {
    case "paid":
      return (
        <Badge variant="success" className="gap-1">
          <CheckCircle className="h-3 w-3" />
          Paid
        </Badge>
      );
    case "due":
      // "Invoiced" when a Sent invoice exists; "Due" when threshold met but not yet invoiced
      return hasMatchingInvoice ? (
        <Badge variant="default" className="gap-1">
          <FileText className="h-3 w-3" />
          Invoiced
        </Badge>
      ) : (
        <Badge variant="warning" className="gap-1">
          <Clock className="h-3 w-3" />
          Due
        </Badge>
      );
    case "overdue":
      return (
        <Badge variant="destructive" className="gap-1">
          <AlertCircle className="h-3 w-3" />
          Overdue
        </Badge>
      );
    case "pending":
      return (
        <Badge variant="secondary" className="gap-1">
          <Clock className="h-3 w-3" />
          Scheduled
        </Badge>
      );
    case "future":
      return (
        <Badge variant="outline" className="gap-1">
          <CalendarDays className="h-3 w-3" />
          Future
        </Badge>
      );
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

function getInvoiceStatusBadge(status: Invoice["status"]) {
  switch (status) {
    case "Paid":
      return <Badge variant="success">Paid</Badge>;
    case "Sent":
      return <Badge variant="default">Sent</Badge>;
    case "Overdue":
      return <Badge variant="destructive">Overdue</Badge>;
    case "Draft":
      return <Badge variant="secondary">Draft</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

// ============================================================================
// Summary Bar
// ============================================================================

interface SummaryBarProps {
  contractValue: number;
  invoicedToDate: number;
  outstanding: number;
  nextBillingEvent: BillingMilestone | null;
  formatCurrency: (v: number) => string;
}

function SummaryBar({
  contractValue,
  invoicedToDate,
  outstanding,
  nextBillingEvent,
  formatCurrency,
}: SummaryBarProps) {
  // Determine overall status
  const overallStatus = useMemo(() => {
    if (outstanding <= 0) return "complete";
    if (
      nextBillingEvent &&
      (nextBillingEvent.status === "overdue")
    ) {
      return "overdue";
    }
    return "on-track";
  }, [outstanding, nextBillingEvent]);

  const statusBadge = useMemo(() => {
    switch (overallStatus) {
      case "complete":
        return (
          <Badge variant="success" className="gap-1">
            <CheckCircle className="h-3 w-3" />
            Complete
          </Badge>
        );
      case "overdue":
        return (
          <Badge variant="destructive" className="gap-1">
            <AlertCircle className="h-3 w-3" />
            Overdue
          </Badge>
        );
      default:
        return (
          <Badge variant="default" className="gap-1">
            <TrendingUp className="h-3 w-3" />
            On Track
          </Badge>
        );
    }
  }, [overallStatus]);

  return (
    <div className="flex flex-wrap items-center gap-6 px-5 py-4 bg-neutral-50 rounded-lg border border-neutral-200">
      {/* Contract Value */}
      <div className="flex flex-col">
        <span className="text-xs text-muted-foreground uppercase tracking-wide mb-0.5">
          Contract Value
        </span>
        <span className="text-base font-semibold text-neutral-900">
          {formatCurrency(contractValue)}
        </span>
      </div>

      <div className="h-8 w-px bg-border hidden sm:block" />

      {/* Invoiced to Date */}
      <div className="flex flex-col">
        <span className="text-xs text-muted-foreground uppercase tracking-wide mb-0.5">
          Invoiced to Date
        </span>
        <span className="text-base font-semibold text-neutral-900">
          {formatCurrency(invoicedToDate)}
        </span>
      </div>

      <div className="h-8 w-px bg-border hidden sm:block" />

      {/* Outstanding */}
      <div className="flex flex-col">
        <span className="text-xs text-muted-foreground uppercase tracking-wide mb-0.5">
          Outstanding
        </span>
        <span
          className={`text-base font-semibold ${
            outstanding > 0 ? "text-warning-600" : "text-success-600"
          }`}
        >
          {formatCurrency(outstanding)}
        </span>
      </div>

      <div className="h-8 w-px bg-border hidden sm:block" />

      {/* Next Billing Event */}
      <div className="flex flex-col">
        <span className="text-xs text-muted-foreground uppercase tracking-wide mb-0.5">
          Next Billing Event
        </span>
        {nextBillingEvent ? (
          <div className="flex flex-col">
            <span className="text-base font-semibold text-neutral-900">
              {nextBillingEvent.name}
            </span>
            <span className="text-xs text-muted-foreground mt-0.5">
              {nextBillingEvent.triggerPercent !== undefined
                ? `At ${nextBillingEvent.triggerPercent}% complete`
                : formatDate(nextBillingEvent.dueDate)}
            </span>
          </div>
        ) : (
          <span className="text-base font-semibold text-muted-foreground">None</span>
        )}
      </div>

      <div className="h-8 w-px bg-border hidden sm:block" />

      {/* Status */}
      <div className="flex flex-col">
        <span className="text-xs text-muted-foreground uppercase tracking-wide mb-0.5">
          Status
        </span>
        <div className="mt-0.5">{statusBadge}</div>
      </div>
    </div>
  );
}

// ============================================================================
// Billing Schedule Table
// ============================================================================

interface BillingScheduleProps {
  milestones: BillingMilestone[];
  invoices: Invoice[];
  completionPercent: number;
  billingTriggerType?: BillingTriggerType;
  onGenerateInvoice: (milestone: BillingMilestone) => void;
  formatCurrency: (v: number) => string;
}

function BillingScheduleTable({
  milestones,
  invoices,
  completionPercent,
  billingTriggerType = 'date',
  onGenerateInvoice,
  formatCurrency,
}: BillingScheduleProps) {
  const rows = useMemo(
    () =>
      milestones.map((m) => {
        const invoicesForMilestone = invoices.filter((inv) =>
          inv.milestoneId !== undefined
            ? inv.milestoneId === m.id
            : Math.abs(inv.amount - m.amount) < 0.01,
        );
        const hasMatchingInvoice = invoicesForMilestone.some(
          (inv) => inv.status === "Sent" || inv.status === "Paid",
        );
        return {
          ...m,
          displayStatus: getMilestoneDisplayStatus(m, invoices, completionPercent, billingTriggerType),
          hasMatchingInvoice,
        };
      }),
    [milestones, invoices, completionPercent, billingTriggerType]
  );

  // "Generate Invoice" is only shown when a milestone needs action — i.e. it
  // hasn't been invoiced yet. "due" can mean threshold met OR already invoiced;
  // the hasMatchingInvoice flag on each row differentiates these two cases.
  const isActionable = (status: BillingMilestoneStatus, hasInvoice: boolean) =>
    !hasInvoice && (status === "due" || status === "overdue");

  if (milestones.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <CalendarDays className="h-10 w-10 mb-3 opacity-40" />
        <p className="text-sm">No billing milestones defined.</p>
        <p className="text-xs mt-1">Add milestones in the Sales Budget to build a billing schedule.</p>
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Milestone</TableHead>
            <TableHead>Trigger</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => {
            // Use project-level billingTriggerType — not the (removed) per-milestone triggerType
            const isPercent = billingTriggerType === "percent-complete";
            const thresholdMet =
              isPercent &&
              row.triggerPercent !== undefined &&
              completionPercent >= row.triggerPercent;

            return (
              <TableRow key={row.id}>
                <TableCell className="font-medium">{row.name}</TableCell>

                {/* ── Trigger column (9.2) ── */}
                <TableCell>
                  {isPercent ? (
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-1.5">
                        {/* Coloured dot: green = threshold met, grey = not yet */}
                        <span
                          className={`inline-block h-2 w-2 rounded-full flex-shrink-0 ${
                            thresholdMet ? "bg-success-500" : "bg-neutral-300"
                          }`}
                        />
                        <span className="text-sm text-foreground">
                          At {row.triggerPercent}%
                          <span
                            className={`ml-1 text-xs font-medium ${
                              thresholdMet ? "text-success-600" : "text-neutral-500"
                            }`}
                          >
                            · project at {completionPercent}%
                          </span>
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground pl-3.5 hidden">
                        At {row.triggerPercent}%
                        <span
                          className={`ml-1 font-medium ${
                            thresholdMet ? "text-success-600" : "text-neutral-500"
                          }`}
                        >
                          · project at {completionPercent}%
                        </span>
                      </div>
                      {row.dueDate && (
                        <div className="text-xs text-muted-foreground pl-3.5">
                          Deadline: {formatDate(row.dueDate)}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="text-sm text-foreground">
                      {formatDate(row.dueDate)}
                    </span>
                  )}
                </TableCell>

                <TableCell className="text-right font-medium">
                  {formatCurrency(row.amount)}
                </TableCell>
                <TableCell>{getMilestoneStatusBadge(row.displayStatus, row.hasMatchingInvoice)}</TableCell>
                <TableCell className="text-right">
                  {isActionable(row.displayStatus, row.hasMatchingInvoice) ? (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onGenerateInvoice(row)}
                            className="gap-1"
                          >
                            <Receipt className="h-3.5 w-3.5" />
                            Generate Invoice
                            <ArrowRight className="h-3 w-3" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          Create an invoice for this billing milestone
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

// ============================================================================
// Invoice History Table (reused from T&M pattern)
// ============================================================================

interface InvoiceHistoryProps {
  invoices: Invoice[];
  formatCurrency: (v: number) => string;
}

function InvoiceHistoryTable({ invoices, formatCurrency }: InvoiceHistoryProps) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const filtered = useMemo(
    () =>
      invoices.filter((inv) => {
        const matchesStatus =
          statusFilter === "all" || inv.status === statusFilter;
        const q = search.toLowerCase();
        const matchesSearch =
          !q ||
          inv.invoiceNumber?.toLowerCase().includes(q) ||
          inv.description.toLowerCase().includes(q);
        return matchesStatus && matchesSearch;
      }),
    [invoices, search, statusFilter]
  );

  const totals = useMemo(() => {
    const invoicedTotal = invoices
      .filter((i) => i.status !== "Draft")
      .reduce((s, i) => s + i.amount, 0);
    const paidTotal = invoices
      .filter((i) => i.status === "Paid")
      .reduce((s, i) => s + i.amount, 0);
    const outstandingTotal = invoices
      .filter((i) => i.status === "Sent" || i.status === "Overdue")
      .reduce((s, i) => s + i.amount, 0);
    return { invoicedTotal, paidTotal, outstandingTotal };
  }, [invoices]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Invoice Documents</CardTitle>
        <CardDescription>All invoices raised against this project</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Totals strip */}
        {invoices.length > 0 && (
          <div className="flex flex-wrap gap-6 p-3 bg-neutral-50 rounded-md border border-neutral-200 text-sm">
            <div>
              <span className="text-muted-foreground">Total Invoiced:</span>
              <span className="ml-2 font-semibold">
                {formatCurrency(totals.invoicedTotal)}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Paid:</span>
              <span className="ml-2 font-semibold text-success-600">
                {formatCurrency(totals.paidTotal)}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Outstanding:</span>
              <span className="ml-2 font-semibold text-warning-600">
                {formatCurrency(totals.outstandingTotal)}
              </span>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search invoices..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-9"
            />
            {search && (
              <Button
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                onClick={() => setSearch("")}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="Draft">Draft</SelectItem>
              <SelectItem value="Sent">Sent</SelectItem>
              <SelectItem value="Paid">Paid</SelectItem>
              <SelectItem value="Overdue">Overdue</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Invoice #</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Due</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                    <Receipt className="h-10 w-10 mx-auto mb-3 opacity-40" />
                    <p className="text-sm">
                      {invoices.length === 0
                        ? "No invoices have been raised yet."
                        : "No invoices match your filters."}
                    </p>
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((inv) => (
                  <TableRow key={inv.id}>
                    <TableCell className="font-medium">
                      {inv.invoiceNumber ?? `#${inv.id}`}
                    </TableCell>
                    <TableCell>{formatDate(inv.date)}</TableCell>
                    <TableCell>{inv.description}</TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(inv.amount)}
                    </TableCell>
                    <TableCell>{formatDate(inv.due)}</TableCell>
                    <TableCell>{getInvoiceStatusBadge(inv.status)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Main Component — FixedFeeBillingTab
// ============================================================================

export function FixedFeeBillingTab({
  contractValue,
  milestones,
  invoices,
  projectName: _projectName,
  completionPercent,
  billingTriggerType = 'date',
  onGenerateInvoice,
  onSettingsClick,
  formatCurrency,
}: FixedFeeBillingTabProps) {
  // Derived summary values
  const invoicedToDate = useMemo(
    () =>
      invoices
        .filter((i) => i.status === "Sent" || i.status === "Paid")
        .reduce((s, i) => s + i.amount, 0),
    [invoices]
  );

  const outstanding = useMemo(
    () => Math.max(0, contractValue - invoicedToDate),
    [contractValue, invoicedToDate]
  );

  const nextBillingEvent = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Re-derive live status for all milestones using completionPercent and project-level trigger type
    const withStatus = milestones.map((m) => ({
      ...m,
      liveStatus: getMilestoneDisplayStatus(m, invoices, completionPercent, billingTriggerType),
    }));

    // Exclude already-paid or invoiced milestones
    const actionable = withStatus.filter(
      (m) => m.liveStatus === "pending" || m.liveStatus === "due" || m.liveStatus === "overdue"
    );

    // Prefer overdue milestones first
    const overdue = actionable.filter((m) => m.liveStatus === "overdue");
    if (overdue.length > 0) return overdue[0];

    // For % Complete milestones that are "due", surface the one with the lowest threshold
    const duePct = actionable
      .filter((m) => m.liveStatus === "due" && billingTriggerType === "percent-complete")
      .sort((a, b) => (a.triggerPercent ?? 0) - (b.triggerPercent ?? 0));
    if (duePct.length > 0) return duePct[0];

    // For date milestones, return the next upcoming one
    const futureDate = actionable
      .filter((m) => billingTriggerType === "date" && m.dueDate)
      .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())
      .filter((m) => new Date(m.dueDate).getTime() >= today.getTime());
    if (futureDate.length > 0) return futureDate[0];

    // Fall back to any pending % Complete milestone (lowest threshold)
    const pendingPct = actionable
      .filter(() => billingTriggerType === "percent-complete")
      .sort((a, b) => (a.triggerPercent ?? 0) - (b.triggerPercent ?? 0));
    return pendingPct[0] ?? null;
  }, [milestones, invoices, completionPercent, billingTriggerType]);

  return (
    <div className="space-y-6">
      {/* 3.1 — Summary Bar */}
      <SummaryBar
        contractValue={contractValue}
        invoicedToDate={invoicedToDate}
        outstanding={outstanding}
        nextBillingEvent={nextBillingEvent}
        formatCurrency={formatCurrency}
      />

      {/* 3.2 — Billing Schedule */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Billing Schedule</CardTitle>
              <CardDescription>
                Milestones from the Sales Budget. Generate an invoice directly from
                any actionable row.
              </CardDescription>
            </div>
            {onSettingsClick && (
              <Button variant="outline" size="sm" onClick={onSettingsClick}>
                <Settings2 className="h-4 w-4 mr-2" />
                Billing Settings
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <BillingScheduleTable
            milestones={milestones}
            invoices={invoices}
            completionPercent={completionPercent}
            billingTriggerType={billingTriggerType}
            onGenerateInvoice={onGenerateInvoice}
            formatCurrency={formatCurrency}
          />
        </CardContent>
      </Card>

      {/* 3.3 — Invoice History */}
      <InvoiceHistoryTable invoices={invoices} formatCurrency={formatCurrency} />
    </div>
  );
}
